import { create } from 'zustand'
import { Api } from '../api/api'

// Типы данных для инвентаря
export interface InventoryAnalytics {
  all_items_count: number;
  market_price_inventory: number;
  user_price_inventory: number;
  size_inventory: Array<{ us_size: string; items_count: number }>;
  article_inventory: Array<{ article: string; items_count: number }>;
  brand_inventory: Array<{ brand: string; items_count: number }>;
  model_inventory: Array<{ brand: string; model: string; items_count: number }>;
  article_and_size_inventory: Array<{ article: string; us_size: string; items_count: number }>;
}

// Типы данных для продаж
export interface SalesAnalytics {
  sales_dates: Array<{ day: string; sales_count: number; total_profit: number }>;
  sales_by_sizes: Array<{ sales_count: number; size: string }>;
  sales_by_brands: Array<{ sales_count: number; brand: string }>;
  sales_by_articles: Array<{ sales_count: number; article: string }>;
  sales_by_models: Array<{ sales_count: number; model: string; brand: string }>;
  sales_by_articles_and_sizes: Array<{ sales_count: number; article: string; size: string }>;
  best_sales: Array<{ profit_sum: number; model: string; color: string; article: string; size: string }>;
  profit_sum: number;
  abs_profit_sum: number;
  sales_sum: number;
}

interface StatisticsState {
  inventoryData: InventoryAnalytics | null;
  salesData: SalesAnalytics | null;
  loading: boolean;
  error: string | null;
  fetchInventoryAnalytics: (period?: 'week' | 'month' | 'year') => Promise<void>;
  fetchSalesAnalytics: (period?: 'week' | 'month' | 'year') => Promise<void>;
  fetchStatistics: (inventoryPeriod?: 'week' | 'month' | 'year', salesPeriod?: 'week' | 'month' | 'year') => Promise<void>;  
  resetStatisticsState: () => void;
}

const useStatisticsStore = create<StatisticsState>((set, get) => {
  const api = new Api();
  
  return {
    inventoryData: null,
    salesData: null,
    loading: false,
    error: null,
    
    fetchInventoryAnalytics: async (period) => {
      try {
        const state = get();
        if (state.loading) return; // Предотвращаем повторные запросы
        
        set({ loading: true, error: null });
        
        const response = await api.getAnalyticInventory(period);
        
        if (response?.error) {
          throw new Error(response.error);
        }
        
        set({ inventoryData: response, loading: false });
      } catch (error: any) {
        console.error('Ошибка при загрузке статистики инвентаря:', error);
        set({ error: error.message || 'Не удалось загрузить данные инвентаря', loading: false });
      }
    },
    
    fetchSalesAnalytics: async (period) => {
      try {
        const state = get();
        if (state.loading) return; // Предотвращаем повторные запросы
        
        set({ loading: true, error: null });
        
        const currentPeriod = period || 'month';
        
        // Загружаем все данные о продажах параллельно
        const [
          salesDatesResponse,
          profitResponse,
          absProfitResponse,
          allSalesResponse,
          salesBySizeResponse,
          salesByBrandResponse,
          salesByArticleResponse,
          salesByModelResponse,
          salesByArticleAndSizeResponse,
          bestSalesResponse
        ] = await Promise.all([
          api.getSalesDates(currentPeriod),
          api.getSalesProfit(currentPeriod),
          api.getSalesAbsProfit(currentPeriod),
          api.getAllSales(currentPeriod),
          api.getSalesBySize(currentPeriod),
          api.getSalesByBrand(currentPeriod),
          api.getSalesByArticle(currentPeriod),
          api.getSalesByModel(currentPeriod),
          api.getSalesByArticleAndSize(currentPeriod),
          api.getBestSales(currentPeriod)
        ]);
        
        // Проверяем на ошибки
        const responses = [
          salesDatesResponse,
          profitResponse,
          absProfitResponse,
          allSalesResponse,
          salesBySizeResponse,
          salesByBrandResponse,
          salesByArticleResponse,
          salesByModelResponse,
          salesByArticleAndSizeResponse,
          bestSalesResponse
        ];
        
        const errorResponse = responses.find(resp => resp?.error);
        if (errorResponse) {
          throw new Error(errorResponse.error);
        }
        
        // Собираем объект статистики продаж
        const salesData: SalesAnalytics = {
          sales_dates: salesDatesResponse?.data || [],
          profit_sum: profitResponse?.profit || 0,
          abs_profit_sum: absProfitResponse?.profit_abs || 0,
          sales_sum: allSalesResponse?.sales_sum || 0,
          sales_by_sizes: salesBySizeResponse?.data || [],
          sales_by_brands: salesByBrandResponse?.data || [],
          sales_by_articles: salesByArticleResponse?.data || [],
          sales_by_models: salesByModelResponse?.data || [],
          sales_by_articles_and_sizes: salesByArticleAndSizeResponse?.data || [],
          best_sales: bestSalesResponse?.data || []
        };
        
        set({ salesData, loading: false });
      } catch (error: any) {
        console.error('Ошибка при загрузке статистики продаж:', error);
        set({ error: error.message || 'Не удалось загрузить данные продаж', loading: false });
      }
    },
    
    fetchStatistics: async (inventoryPeriod?: 'week' | 'month' | 'year', salesPeriod?: 'week' | 'month' | 'year') => {
      try {
        const state = get();
        if (state.loading) return; // Предотвращаем повторные запросы
        
        const defaultPeriod = 'month';
        const invPeriod = inventoryPeriod || defaultPeriod;
        const salesPeriod_ = salesPeriod || defaultPeriod;
        
        // Загружаем оба типа статистики
        await Promise.all([
          get().fetchInventoryAnalytics(invPeriod),
          get().fetchSalesAnalytics(salesPeriod_)
        ]);
        
      } catch (error: any) {
        console.error('Ошибка при загрузке статистики:', error);
        set({ error: error.message || 'Не удалось загрузить данные статистики', loading: false });
      }
    },
    

    
    resetStatisticsState: () => {
      set({
        inventoryData: null,
        salesData: null,
        loading: false,
        error: null
      });
    }
  };
});

export default useStatisticsStore; 